// Global imports.
import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { Helmet } from 'react-helmet';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';

// Views, containers and other components.
import MessageModal, { messageShape } from './containers/MessageModal';

// Stylesheets and media.
import styles from './App.module.css';

// Utils, actions, sagas, etc.
import {
    consentChanged,
    debounce,
    initializeTracking,
    initializeTrackingGA4,
    isTokenPayloadValid,
    replaceNonWordCharacters,
    replaceMultipleSlashes,
    replaceNotAllowedSpecialChars,
    slugify,
    componentLoader,
    objectEmpty,
} from './utils/utils';
import { historyShape } from './utils/routerPropTypes';
import { storage } from './utils/storage';
import { subjectShape, gradeShape, stateShape, topicShape } from './utils/apiPropTypes';
import { vidisAuthentication, showLoader } from './routes/Account/actions';
import Loader from './views/Loader';

// Lazy imports.
const ContentWrapper = lazy(() => componentLoader(() => import('./containers/ContentWrapper')));

const ucEventName = process.env.REACT_APP_USER_CENTRICS_CONSENT_EVENT_NAME;

class App extends React.PureComponent {

    constructor(props) {
        super(props);

        const { messages } = this.props;

        this.state = {
            showMessageModal: !!messages && messages.length > 0,
            gaInitialized: initializeTracking(),
            ga4Initialized: initializeTrackingGA4(),
            consents: storage.getItem('ucData') ? JSON.parse(storage.getItem('ucData'))?.consent.services : [],
        };

        this.setShowMessageModalDebounced = debounce((currentMessages) => {
            this.setState({
                showMessageModal: !!currentMessages && currentMessages.length > 0,
            });
        }, 200);

        window.addEventListener(ucEventName, (event) => {
            const { consents } = this.state;

            // OP9244: referrer should remain the same after page reloaded
            storage.setItem('referrer', document.referrer);
            // OLC-8713: reload webpage when consents have changed
            if (!objectEmpty(consents) && (!!consentChanged(event.detail.services, consents, 'Google Analytics')
                || !!consentChanged(event.detail.services, consents, 'Hotjar'))) {
                window.location.reload();

            }
            if (consents.length < 1) {
                this.setState({
                    consents: !!storage.getItem('ucData')
                        ? JSON.parse(storage.getItem('ucData'))?.conesnt.services : [],
                });
            }
        });
    }

    componentDidMount() {
        const { gaInitialized, ga4Initialized } = this.state;
        if (!gaInitialized) {
            this.setState({ gaInitialized: initializeTracking() });
        }
        if (!ga4Initialized) {
            this.setState({ ga4Initialized: initializeTrackingGA4() });
        }
    }

    componentDidUpdate(prevProps) {
        const { messages } = this.props;

        if ((!!messages && !prevProps.messages)
                || (!!messages && !!prevProps.messages && messages.length !== prevProps.messages.length)) {
            this.setShowMessageModalDebounced(messages);
        }
    }

    closeMessageModal = () => {
        const { messages } = this.props;

        this.setState({
            showMessageModal: false,
        },
        () => this.setShowMessageModalDebounced(messages));
    }

    checkForVidisCallback = () => {
        const { actions, history, authenticated, loginLoader } = this.props;

        // after vidis authencation, login the user in our system
        const params = queryString.parse(history.location.search);
        // check if loginLoader is on true, if that is the case then dont do the logic again
        if (params.code && !authenticated && !loginLoader) {
            actions.showLoader(true);

            const payload = { code: params.code };
            actions.vidisAuthentication(payload, history);
        }
    }

    render() {
        const {
            messages,
            canonical,
            history,
            subjects,
            chosenSubjectId,
            grades,
            states,
            chosenGradeId,
            chosenStateId,
            topics,
            chosenMainTopicId,
            chosenBetweenTopicId,
            chosenUnderTopicId,
            redirectUrl,
            selectedSeoTopic,
            authenticated,
            loginLoader,
        } = this.props;
        const { showMessageModal } = this.state;

        this.checkForVidisCallback();

        const pathname = decodeURI(window.location.pathname).replace(process.env.REACT_APP_BASENAME, '');
        if (!!redirectUrl && pathname !== redirectUrl) {
            return (
                <Redirect
                    from={pathname}
                    to={redirectUrl}
                    state={{ status: 301 }}
                />
            );
        }

        if (pathname === '/downloads') {
            if (window.location.href.includes('schuelerhilfe-olc.de')) {
                window.location.href = process.env.REACT_APP_BASEURL_VIDIS.concat(process.env.REACT_APP_BASENAME, '/');
            } else {
                window.location.href = window.location.href.includes('.at/')
                    ? process.env.REACT_APP_BASEURL.replace('.de', '.at')
                        .concat(process.env.REACT_APP_BASENAME, '/')
                    : process.env.REACT_APP_BASEURL.concat(process.env.REACT_APP_BASENAME, '/');
            }
        }

        const subject = subjects.find((s) => s.id === chosenSubjectId);
        const subjectName = subject !== undefined ? subject.name : '';

        // here the name of subject and under topic are checked
        // if there was a rename in them then redirect to the current fixed name
        const splitPathName = pathname.split('/').filter((name) => !!name);
        if (splitPathName.length > 0 && !!subject && pathname.match(/\/\d+-/g)) {
            const splitIds = pathname.match(/\/\d+-/g).map((match) => match.replace('/', '').replace('-', ''));
            const subjectId = parseInt(splitIds[0], 10);

            const incommingSubjectUrl = '/'.concat(splitPathName[0]);
            const subjectUrl = `/${subject.id}-${slugify(subject.name)}`;

            if (splitPathName.length === 1 && subjectId === chosenSubjectId && incommingSubjectUrl !== subjectUrl) {
                return (
                    <Redirect
                        from={pathname}
                        to={subjectUrl}
                        state={{ status: 301 }}
                    />
                );
            }
        }

        const regex = new RegExp('[äöüß²³()„“"|]|/{2,}', 'g');
        const decodedUrl = decodeURI(pathname);
        const parsedUrl = replaceNotAllowedSpecialChars(
            replaceNonWordCharacters(decodeURI(replaceMultipleSlashes(pathname))),
        );
        if (!!decodedUrl.match(regex) && decodedUrl !== parsedUrl) {
            return (
                <Redirect
                    from={pathname}
                    to={parsedUrl}
                    state={{ status: 301 }}
                />
            );
        }

        const grade = grades.find((g) => g.id === chosenGradeId);
        const gradeName = grade !== undefined ? grade.name : '';

        const state = states.find((s) => s.id === chosenStateId);
        const stateName = state !== undefined ? state.name : '';

        const mainTopic = topics.find((mt) => mt.id === chosenMainTopicId);
        const mainTopicName = mainTopic !== undefined ? mainTopic.name : '';

        const betweenTopic = topics.find((bt) => bt.id === chosenBetweenTopicId);
        const betweenTopicName = betweenTopic !== undefined ? betweenTopic.name : '';

        const underTopic = topics.find((ut) => ut.id === chosenUnderTopicId);
        const underTopicName = underTopic !== undefined ? underTopic.name : '';

        const seoTopicName = selectedSeoTopic !== null ? selectedSeoTopic.name : '';

        const breadcrumbElements = [];
        const baseUrl = window.location.href.includes('schuelerhilfe-olc.de') ? process.env.REACT_APP_BASEURL_VIDIS
            : process.env.REACT_APP_BASEURL;
        let position = 1;
        let name = '';
        let breadcrumbUrlBuilder = '';

        if (process.env.REACT_APP_BASENAME.trim() !== '') {
            const baseBreadcrumbElement = {
                '@type': 'ListItem',
                position,
                item: {
                    '@id': baseUrl,
                    name: 'Schülerhilfe',
                },
            };
            breadcrumbElements.push(baseBreadcrumbElement);
            position += 1;
        }

        const bibliothekBreadcrumbElement = {
            '@type': 'ListItem',
            position,
            item: {
                '@id': authenticated ? `${baseUrl}${process.env.REACT_APP_BASENAME}` : `${baseUrl}/onlinelerncenter/`,
                name: 'Online-LernCenter',
            },
        };

        breadcrumbElements.push(bibliothekBreadcrumbElement);
        position += 1;

        breadcrumbUrlBuilder += baseUrl + process.env.REACT_APP_BASENAME;

        // Split URL into its components, filter empty and undefined values
        const url = canonical !== '' ? canonical : history.location.pathname;
        const urlParts = url.split('/').filter((e) => !!e && e.trim().length > 0);

        if (urlParts.length > 0) {
            urlParts.forEach((urlPart, index) => {
                breadcrumbUrlBuilder += `/${urlPart}`;
                if (canonical !== '') {
                    const loggedIn = isTokenPayloadValid(storage.getItem('token'));
                    if (loggedIn) {
                        if (index === 0) {
                            name = subjectName;
                        } else if (index === 1) {
                            name = gradeName;
                        } else if (index === 2) {
                            if (!!grade && grade.workPackage) {
                                name = stateName;
                            } else {
                                name = mainTopicName;
                            }
                        } else if (index === 3) {
                            if (!!grade && grade.workPackage) {
                                name = mainTopicName;
                            } else if (!!betweenTopic) {
                                name = betweenTopicName;
                            } else {
                                name = underTopicName;
                            }
                        } else if (index === 4) {
                            if (!!grade && grade.workPackage) {
                                if (!!betweenTopic) {
                                    name = betweenTopicName;
                                } else {
                                    name = underTopicName;
                                }
                            } else {
                                name = underTopicName;
                            }
                        } else if (index > 4) {
                            name = underTopicName;
                        }
                    } else if (!loggedIn && index === 0) {
                        name = subjectName;
                    } else if (!loggedIn && index === 1 && !!gradeName) {
                        name = gradeName;
                    } else if (!loggedIn && index === 1 && !!seoTopicName) {
                        name = seoTopicName;
                    }
                } else {
                    name = urlPart[0].toUpperCase() + urlPart.substring(1);
                    switch (name) {
                    case 'Abos-und-preise':
                        name = 'Abos und Preise';
                        break;
                    case 'Agbs':
                        name = 'AGB';
                        break;
                    case 'Feedback':
                        name = 'Kontakt';
                        break;
                    case 'Faqs':
                        name = 'FAQ';
                        break;
                    case 'Live-forum':
                        name = 'Webinare';
                        break;
                    case 'Passwort-anfordern':
                        name = 'Neues Passwort anfordern';
                        break;
                    default:
                        // do nothing.
                    }
                }

                breadcrumbElements.push({
                    '@type': 'ListItem',
                    position: index + position,
                    item: {
                        '@id': breadcrumbUrlBuilder,
                        name,
                    },
                });
            });
        }

        const breadcrumbData = {
            '@context': 'http://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbElements,
        };

        const JsonLd = ({ data }) => (
            <script
                type="application/ld+json"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
            />
        );

        const canonicalBaseUrl = window.location.href.includes('schuelerhilfe-olc.de')
            ? `${process.env.REACT_APP_BASEURL_VIDIS}${process.env.REACT_APP_BASENAME}`
            : `${process.env.REACT_APP_BASEURL}${process.env.REACT_APP_BASENAME}`;
        const appPublicPrefix = window.location.href.indexOf('.at/') > -1
            ? canonicalBaseUrl.replace('.de/', '.at/')
            : canonicalBaseUrl;

        return (
            <div className={styles.app}>
                <Helmet>
                    <link rel="canonical" href={appPublicPrefix + url} />
                    {window.location.href.indexOf('.at/') > -1 && (
                        <meta name="robots" content="noindex" />
                    )}
                    {window.location.href.indexOf('.de/') > -1 && (
                        <meta name="robots" content="index, follow" />
                    )}
                </Helmet>

                <JsonLd data={breadcrumbData} />

                {loginLoader && (
                    <div className={styles.loginLoaderContainer}>
                        <Loader />
                    </div>
                )}
                <Suspense fallback={<div />}>
                    <ContentWrapper breadcrumbElements={breadcrumbElements} />
                </Suspense>

                { showMessageModal
                    && (
                        <MessageModal
                            messages={messages}
                            testName="MessageModal"
                            onClose={this.closeMessageModal}
                        />
                    )}
            </div>
        );
    }

}

App.propTypes = {
    actions: PropTypes.objectOf(PropTypes.func).isRequired,
    canonical: PropTypes.string,
    history: historyShape.isRequired,
    messages: PropTypes.arrayOf(messageShape),
    subjects: PropTypes.arrayOf(subjectShape),
    grades: PropTypes.arrayOf(gradeShape),
    states: PropTypes.arrayOf(stateShape),
    topics: PropTypes.arrayOf(topicShape),
    chosenSubjectId: PropTypes.number,
    chosenGradeId: PropTypes.number,
    chosenStateId: PropTypes.number,
    chosenMainTopicId: PropTypes.number,
    chosenBetweenTopicId: PropTypes.number,
    chosenUnderTopicId: PropTypes.number,
    redirectUrl: PropTypes.string,
    selectedSeoTopic: PropTypes.shape(topicShape),
    authenticated: PropTypes.bool.isRequired,
    loginLoader: PropTypes.bool.isRequired,
};

App.defaultProps = {
    canonical: '',
    messages: [],
    subjects: [],
    grades: [],
    states: [],
    topics: [],
    chosenSubjectId: null,
    chosenGradeId: null,
    chosenStateId: null,
    chosenMainTopicId: null,
    chosenBetweenTopicId: null,
    chosenUnderTopicId: null,
    redirectUrl: null,
    selectedSeoTopic: null,
};

const mapStateToProps = (state) => ({
    authenticated: state.account.authenticated,
    canonical: state.library.canonical,
    messages: state.messageModal.messages,
    subjects: state.library.subjects,
    grades: state.library.grades,
    states: state.library.states,
    topics: state.library.topics,
    chosenSubjectId: state.library.chosenSubjectId,
    chosenGradeId: state.library.chosenGradeId,
    chosenStateId: state.library.chosenStateId,
    chosenMainTopicId: state.library.chosenMainTopicId,
    chosenBetweenTopicId: state.library.chosenBetweenTopicId,
    chosenUnderTopicId: state.library.chosenUnderTopicId,
    redirectUrl: state.library.redirectUrl,
    selectedSeoTopic: state.library.selectedSeoTopic,
    loginLoader: state.account.showLoader,
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators({
        vidisAuthentication,
        showLoader,
    }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(App));
